/* eslint-disable no-useless-catch */
import * as Sentry from '@sentry/vue'
import PresentationApi from '@/api/presentation'
import UploadApi from '@/api/upload'
import Socket from '@/socket'
import router from '@/router'
import Utils from '@/util'
import Constant from '@/constant'
import i18nPresenter from '@/i18nPresenter'
import { EVENT_CATEGORIES_BY_ROUTE_NAME } from '@/constant/routes'
import configs from '@/config'
import { fetchCanvasBlocksForSlides } from '@/utils/CanvasBlocksLoader.utils'
import {
  maybeArmFirstPresentTour,
  FIRST_PRESENT_DEMO_FLAG,
} from '@/components-v2/onboarding/onboarding.utils'

const KV_SYNC_SUBSCRIBE_KEY_ACTION = 'kvSync/subscribeKeyAction'

const setPresentationAction = ({ commit }, presentation) => {
  commit('setPresentation', presentation)
}

const createNewPresentationApiAction = async ({ dispatch }, payload) => {
  const newPresentation = await PresentationApi.createNewPresentation(payload)
  dispatch('afterCreateNewPresentationAction', newPresentation)
  return newPresentation
}

// BOO-39: arm the guided first-present tour when a presentation is created from
// an onboarding flow. The origin travels in the create's `queryParams.deepLinkSource`
// (e.g. `onboarding-ikea-checklist` for AI-create, `onboarding-no-question` for a
// slide-type pick). Both gates (onboarding origin + Unleash variant) live in
// `maybeArmFirstPresentTour`; this wrapper only resolves the inputs and guards
// so a failure never blocks the create/navigation.
//
// BOO-39 (round 13): the explicit backend presentation-count check is dropped.
// `isOnboardingOrigin(deepLinkSource)` already implies first-ever presentation
// because the onboarding app is blocked for users with any legacy-onboarding
// signal (`hasLegacyOnboardingSignal`), which includes `hasCreatedPresentation`.
const armFirstPresentTourFromQueryParams = (queryParams, rootGetters, currentUser) => {
  try {
    const deepLinkSource = queryParams && queryParams.deepLinkSource
    const getVariant = rootGetters['featureToggles/getVariant']
    const presentDemoVariant = getVariant(FIRST_PRESENT_DEMO_FLAG)?.name
    // BOO-63: pass currentUser so the skipped-onboarding cohort is excluded from the
    // first-present tour (they get home-create-first instead).
    maybeArmFirstPresentTour({
      deepLinkSource,
      presentDemoVariant,
      currentUser,
    })
  } catch (err) {
    Sentry.addBreadcrumb({
      category: 'first-present-tour',
      message: 'armFirstPresentTourFromQueryParams failed',
      data: { error: err?.message || String(err) },
      level: 'warning',
    })
  }
}

// AHA-43207: deep-link creator pages (e.g. /apps/create-from-ai) pass
// replaceHistory:true so the transient creator route is removed from history —
// otherwise the editor's Back button returns to it and re-creates a presentation
// on every click. Default is push for all normal callers. Extracted as a helper
// so createNewPresentationAction's cognitive complexity is unchanged.
const navigateToEditor = (target, replaceHistory) => {
  const navigation = replaceHistory ? router.replace(target) : router.push(target)
  // Vue Router 3.1+ returns a Promise that rejects on a duplicated/aborted
  // navigation (e.g. a guard redirect). Swallow the benign NavigationDuplicated
  // and report anything unexpected to Sentry, rather than re-throwing (which would
  // just resurface as the unhandled rejection we are trying to avoid).
  if (navigation && typeof navigation.catch === 'function') {
    navigation.catch((err) => {
      if (err && err.name === 'NavigationDuplicated') return
      Sentry.captureException(err)
    })
  }
}

const createNewPresentationAction = async (
  {
    state, dispatch, commit, rootGetters,
  },
  {
    trigger, onSuccess, onFailure, queryParams = {}, apiPayload = {}, replaceHistory = false,
  },
) => {
  const { selectedFolderId } = state.selections
  const {
    currentUser, currentAccount, workingMode, presentations,
  } = state
  commit('setIsCreatingNewPresentation', true)
  const language =
    currentUser.rememberSettings && currentUser.rememberSettings.language ? currentUser.rememberSettings.language : null
  try {
    const defaultSlideConfig = rootGetters['slideModule/defaultSlideConfig']
    const payload = {
      name: Constant.presentation.defaultPresentationName,
      accessCode: '',
      socketId: Socket.getSocketId(),
      folderId: selectedFolderId,
      hasDefaultSlide: true,
      fromWorkspace: workingMode === 'team' && trigger !== Constant.Events.TRIGGER.BLANK_FROM_ONBOARDING_V3,
      lang: language,
      defaultSlideConfig,
      ...apiPayload,
    }
    if (currentUser.personalInformation && currentUser.personalInformation.presenterLanguage) {
      payload.language = currentUser.personalInformation.presenterLanguage
    }

    const newPresentation = await dispatch('createNewPresentationApiAction', payload)

    newPresentation.User = currentUser
    // BOO-39: this is the durable chokepoint for EVERY onboarding-origin presentation
    // create — AI-create, direct slide-type pick (Quiz/Poll/…), and any future
    // onboarding entry point all funnel through here. The onboarding app declares a
    // generic origin via the `deepLinkSource` query param (`onboarding-*`); we arm
    // the guided first-present tour gated on: onboarding origin + Unleash variant
    // `present-demo`. Best-effort and never blocks editor navigation. The template
    // deep-link path arms in CreatingPresentationView (not through this action) so
    // there is no risk of double-arming.
    armFirstPresentTourFromQueryParams(queryParams, rootGetters, state.currentUser)
    if (trigger === 'Quiz hints Banner') {
      state.createdPresentationFromQuizHintsBanner = true
    }
    const query = {
      new: true,
      ...queryParams,
    }
    if (state.isPowerPointAddIn) {
      delete query.new
    }

    navigateToEditor({
      name: 'PresentationDetail',
      params: { id: newPresentation.id },
      query,
    }, replaceHistory)
    const teamId = workingMode === 'team' ? currentAccount.id : null
    const isTeamPresentation = workingMode === 'team'
    // In the most case of our tracking data system, eventCategory is the name of the current route
    let eventCategory = ''
    if (router.currentRoute && router.currentRoute.name) {
      eventCategory = EVENT_CATEGORIES_BY_ROUTE_NAME[router.currentRoute.name]
    }
    Utils.mixpanelTracking(eventCategory, 'Create new presentation', trigger, null, {
      presentationId: newPresentation.id,
      teamId,
      isTeamPresentation,
    })
    // doạn track này gây lỗi cần xem lại vấn đề lỗi là gì
    // if (getters.abTestGroup === 2 || getters.abTestGroup === 3) {
    //   const slideTracker = new SlideMixpanelTracker(this.presentation, this.$route.name)
    //   slideTracker.setCustomProps({
    //     isDefaultSlide: true,
    //   })
    //   slideTracker.trackCreateNewSlide(null, null)
    // }
    dispatch('setPresentationsAction', [...presentations, newPresentation])
    if (onSuccess) {
      onSuccess(newPresentation)
    }
  } catch (e) {
    Sentry.captureException(e)
    if (onFailure) {
      onFailure(e)
    }
  } finally {
    commit('setIsCreatingNewPresentation', false)
  }
}

// Used outside the editor (e.g. Home): create a brand-new presentation, import
// the quiz spreadsheet into it, then open the editor on that presentation.
// Per product decision the editor is opened whether the import succeeds or
// fails (a failed import just surfaces a toast and leaves an empty deck).
const importSpreadsheetToNewPresentationAction = async (
  {
    state, dispatch, commit, rootGetters,
  },
  { file },
) => {
  const { selectedFolderId } = state.selections
  const { currentUser, workingMode, presentations } = state
  const language =
    currentUser.rememberSettings && currentUser.rememberSettings.language ? currentUser.rememberSettings.language : null
  const defaultSlideConfig = rootGetters['slideModule/defaultSlideConfig']
  const payload = {
    name: Constant.presentation.defaultPresentationName,
    accessCode: '',
    socketId: Socket.getSocketId(),
    folderId: selectedFolderId,
    // No default slide: the imported quiz slides become the deck's content.
    hasDefaultSlide: false,
    fromWorkspace: workingMode === 'team',
    lang: language,
    defaultSlideConfig,
  }
  if (currentUser.personalInformation && currentUser.personalInformation.presenterLanguage) {
    payload.language = currentUser.personalInformation.presenterLanguage
  }

  // Let creation errors propagate so the caller can show the upload error UI
  // without navigating away.
  const newPresentation = await dispatch('createNewPresentationApiAction', payload)
  newPresentation.User = currentUser
  dispatch('setPresentationsAction', [...presentations, newPresentation])

  try {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('socketId', Socket.getSocketId())
    formData.append('fileSize', file.size)
    formData.append('fileName', file.name)
    formData.append('id', newPresentation.id)

    const resp = await UploadApi.uploadXlsx(formData)
    // Mirror the in-editor import: use whichever import-sheet endpoint this
    // account is on, otherwise the created quiz slides come back in a format
    // the client can't render (blank slide with only a title).
    const useImportSheetV2 = rootGetters['aiChatbotImportPptxPdf/canUseImportSheetV2']
    const importSheet = useImportSheetV2 ? UploadApi.importSpreadsheetV2 : UploadApi.importSpreadsheet
    await importSheet({
      presentationId: newPresentation.id,
      file: resp.body.file.key,
    })
    Utils.showToastSuccess(i18nPresenter.t('Import Done!'))
  } catch (error) {
    Sentry.captureException(error)
    Utils.showToastError(i18nPresenter.t('Please recheck your file or contact support'))
  }

  // Close every import modal before navigating, otherwise the underlying
  // Import (UploadModalV2 / ModalImport) state carries into the new editor and
  // the modal pops open again on top of the freshly created presentation.
  dispatch('setImportSheetNewPresentationModalStateAction', false)
  dispatch('aiChatbotImportPptxPdf/setIsAIChatbotImportModalVisibleV2Action', false, { root: true })
  commit('setOpenImportModal', false)

  navigateToEditor({
    name: 'PresentationDetail',
    params: { id: newPresentation.id },
    // No `new` flag: the deck already has imported content, so it should open
    // like a normal presentation (Present/Preview enabled, no blank-deck onboarding).
    query: {},
  }, false)

  return newPresentation
}

const setMorePresentationsLoadingAction = ({ commit }, payload) => {
  commit('setMorePresentationsLoading', payload)
}

const copyDemoPresentationAfterLoggingInAction = async (_, { shareCode }) => {
  try {
    if (!shareCode) {
      throw new Error('shareCode not found')
    }
    const copiedPresentation = await PresentationApi.copyPresentationToAccountByShareCode({
      id: shareCode,
    })
    if (!copiedPresentation || !copiedPresentation.id) {
      throw new Error('Copied presentation is not returned from api')
    }

    window.location.href = `${configs.presenterDomain}/presentation/${copiedPresentation.id}?source=demo`
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        shareCode,
      },
    })
    // Worst case: if anything goes wrong when user log in to get the demo presentation, we redirect user to the Home page. This is the safety net.
    window.location.href = `${configs.presenterDomain}/apps/home`
  }
}

const patchPresentationAction = ({ commit, state, dispatch }, partialPresentation) => {
  commit('setPresentation', { ...state.presentation, ...partialPresentation })

  // Whenever update reportUrl, dispatch the action to get the report data
  if (partialPresentation.exportData && partialPresentation.exportData.reportUrl) {
    dispatch('presentationReport/getPresentationReport', partialPresentation)
  }
}

const onlyPatchPresentationAction = ({ commit, state }, partialPresentation) => {
  commit('setPresentation', { ...state.presentation, ...partialPresentation })
}

const toggleQRCodePanelAction = ({ commit }, isOpen) => {
  commit('setQRCodeVisible', isOpen)
}

const openAudiencePrivateFeedbackAction = async ({ commit }, presentation) => {
  commit('setIsOpeningPrivateFeedback', true)
  await PresentationApi.openPrivateFeedback(presentation)
  commit('setIsOpeningPrivateFeedback', false)
}

const setPresentFromBeginningAction = async ({ commit, dispatch }, presentation) => {
  const presentationDetail = await PresentationApi.getPresentationDetails(presentation.id)
  const slides = presentationDetail.body.Slides
  slides.sort((a, b) => a.order - b.order)
  const firstSlide = slides[0]
  dispatch('setSlideActiveAction', firstSlide)

  await PresentationApi.updatePresentationByPresentationId(presentation.id, {
    activeSlide: firstSlide.id,
  })

  commit('setPresentFromBeginning', true)
}

const updatePresentationAction = async ({ commit }, payload) => {
  commit('updatePresentation', payload)
}

const fetchPresentationDetailsFromAPIAction = async (_, { presentationId, payloadDataPresentation }) => {
  /**
   * Wrapping the API call so that other module can make the call without importing presentation api
   * which cause circular dependency
   */
  return PresentationApi.getPresentationDetails(presentationId, payloadDataPresentation)
}

const getPresentationByIdActionV2 = async (
  { dispatch, state, rootGetters }, { presentationId, payloadDataPresentation },
) => {
  const detailResp = await dispatch('optimizeEditorLoadModule/getPresentationDetailsWithCacheAction', {
    presentationId,
    payloadDataPresentation,
    maxAgeInSeconds: rootGetters['optimizeEditorLoadModule/maxPresentationCacheTimeInSeconds'],
  })
  const presentation = detailResp.body
  if (presentation.userId !== state.currentUser?.id) {
    // get owner of presentation email for flagging
    await dispatch('featureToggles/handlePresentationLoadFlagUnleash', presentationId)
  }

  const presentFrom = Utils.getParamValue('presentFrom')
  if (presentFrom === 'google-slide') {
    // this subscribe to ahasync logic is supposed to serve Googleslide presentation
    // but I have not found which module consumes them yet. When testing, Googleslides sync
    // seem to work without it. We can consider removing this block later.
    const slideIds = presentation.Slides.map((slide) => slide.id)
    dispatch('subscribeSlidesAction', { slideIds, presentationId })
  }
  return detailResp
}

const getPresentationByIdAction = async (
  { dispatch, rootGetters, state },
  { presentationId, payloadDataPresentation },
) => {
  let res = null
  try {
    // Fire the heavy presentation-detail fetch and the cheap presentation-attribute
    // lookup in parallel instead of sequentially. getPresentationAttributeAction only
    // needs presentationId (already in hand) and reads nothing from the detail response,
    // so it need not wait for it — this frees one full round-trip on every cold editor
    // open (AHA2-121). The attribute action swallows its own errors, so a detail
    // rejection still propagates first and unchanged.
    const attributePromise = dispatch('presentationAttributeModule/getPresentationAttributeAction', presentationId)
    res = await getPresentationByIdActionV2(
      { dispatch, rootGetters, state },
      { presentationId, payloadDataPresentation },
    )
    await attributePromise
    // We can move it upper to avoid reload Themes everytime when we open a new presentation
    // But I prefer to keep it here to get update from offical Theme more frequently,
    // but not frequently as put it in the ThemeSetting.vue (call everytime when open the Design Tabs)
    dispatch('themeModule/getThemesAction')
    dispatch('themeModule/getOfficialThemesAction')
    dispatch('presetModule/getPresetColorPalettesAction')
    dispatch('themeModule/getTeamThemesAction')
    dispatch('remoteControlLink/getRemoteLink', res.body)
    dispatch('skipSlide/getSkipSlidesActions', res.body.id)
    dispatch('slideAttributes/getByKeys', {
      presentationId: res.body.id,
      attributeKeys: ['cloudImportSource'],
    })

    if (rootGetters['featureToggles/canUseNewSlideMarketPlace']) {
      // Fire-and-forget pre-fetch. The action catches its own errors internally
      // (commits FETCH_SLIDE_TYPES_FAILURE); this guard only catches an unhandled
      // rejection if that ever changes — surface it rather than swallowing it.
      dispatch('slideMarketplace/fetchMarketplaceSlideTypes', null, { root: true }).catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Failed to pre-fetch marketplace slide types:', err)
      })
      // Pre-fetch the user's pinned slide types too, so "My slide types" is ready
      // the first time the picker opens instead of briefly flashing the local
      // defaults while the saved list loads.
      dispatch('slideMarketplace/fetchPinnedSlideTypes', null, { root: true }).catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Failed to pre-fetch pinned slide types:', err)
      })
    }

    return res
    // eslint-disable-next-line sonarjs/no-useless-catch
  } catch (err) {
    throw err
  }
}

const updatePresentationByIdAction = async ({ commit }, { id, payload }) => {
  commit('updatePresentation', payload)
  PresentationApi.updatePresentationByPresentationId(id, payload)
}

const subscribeSlidesAction = async ({ dispatch }, payload) => {
  if (!payload.presentationId || !payload.slideIds || configs.region === Constant.euRegion) return
  dispatch(KV_SYNC_SUBSCRIBE_KEY_ACTION, {
    key: `/presentations/${payload.presentationId}/slidesList`,
  })
  payload.slideIds.forEach((slideId) => {
    dispatch(KV_SYNC_SUBSCRIBE_KEY_ACTION, {
      key: `/slides/${slideId}/#`,
    })
  })
}

const subscribeEveryoneAnsweredAction = async ({ dispatch }, { key, callback }) => {
  if (!key || configs.region === Constant.euRegion) return
  dispatch(KV_SYNC_SUBSCRIBE_KEY_ACTION, {
    key,
    callback,
  })
}

const unsubscribeEveryoneAnsweredAction = async ({ dispatch }, key) => {
  if (!key || configs.region === Constant.euRegion) return
  dispatch('kvSync/unsubscribeKeyAction', key)
}

const addSlidesAction = async ({ commit, rootState }, payload) => {
  const { slides, insertAfter: insertAfterParams } = payload

  if (!slides || !slides.length) return

  // If insertAfter is undefined, default to the last slide so new slides are appended at presentation's end.
  let insertAfter
  if (!insertAfterParams && rootState.presentation?.slides?.length) {
    insertAfter = rootState.presentation?.slides?.length - 1
  }

  const slidesWithFetchedCanvasBlocks = await fetchCanvasBlocksForSlides(slides)

  commit('addSlides', {
    slidesToAdd: slidesWithFetchedCanvasBlocks,
    insertAfter,
  })
}

const subscribeRequestAccessSuccessAction = async ({ dispatch }, { presentationId, callback }) => {
  dispatch(KV_SYNC_SUBSCRIBE_KEY_ACTION, {
    key: `/presentations/${presentationId}/access-requests`,
    callback,
  })
}

const unsubscribeRequestAccessSuccessAction = async ({ dispatch }, { key }) => {
  dispatch('kvSync/unsubscribeKeyAction', key)
}

const setExportingTypeAction = ({ commit }, type) => {
  commit('setExportingType', type)
}

const toggleNotificationPanel = ({ commit }) => {
  commit('toggleNotificationPanel')
}

const resetNotificationUnread = ({ commit }) => {
  commit('resetNotificationUnread')
}

export default {
  setPresentationAction,
  createNewPresentationApiAction,
  setPresentation: setPresentationAction,
  createNewPresentationAction,
  importSpreadsheetToNewPresentationAction,
  patchPresentationAction,
  updatePresentation: patchPresentationAction,
  onlyPatchPresentationAction,
  setMorePresentationsLoadingAction,
  openAudiencePrivateFeedbackAction,
  toggleQRCodePanelAction,
  setPresentFromBeginningAction,
  updatePresentationAction,
  fetchPresentationDetailsFromAPIAction,
  getPresentationByIdAction,
  updatePresentationByIdAction,
  subscribeSlidesAction,
  addSlidesAction,
  subscribeEveryoneAnsweredAction,
  unsubscribeEveryoneAnsweredAction,
  subscribeRequestAccessSuccessAction,
  unsubscribeRequestAccessSuccessAction,
  copyDemoPresentationAfterLoggingInAction,
  setExportingTypeAction,
  resetNotificationUnread,
  toggleNotificationPanel,
}
